/**
 * Sidebar v4 — navegación lateral, SOA 10 PRO (pantalla 2e).
 *
 * Ancho 264px (80px plegado), sin borde, sobre el fondo de la app #F4F3EF.
 *   1. Cabecera: logo del cliente (44px, radio 14) + nombre + "con The Crew";
 *      con varios clientes funciona como selector.
 *   2. Nav: Hoy · Trabajo · Plan · Resultados · Cuenta — ítem de 46px, radio
 *      16, activo #111318 / blanco con ícono relleno (FILL 1).
 *   3. Conversación: tarjeta blanca (radio 18) con el último mensaje del EC.
 *   4. Pie: menú de usuario (Configuración / Cerrar sesión).
 *
 * Es cambio de piel: mismas zonas, mismos datos (useSidebarBadges,
 * useChatPreview) y mismas rutas que la versión anterior.
 */

import { NavLink, Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext.jsx";
import { withPrefetch } from "../lib/prefetch.js";
import { ZONAS } from "../lib/navZones.js";
import Icon from "./Icon.jsx";

function iniciales(nombre = "") {
  const partes = String(nombre).trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  return (partes[0][0] + (partes[1]?.[0] || "")).toUpperCase();
}

// badges / chatPreview vienen de Layout (que los comparte con la barra
// inferior móvil) para no duplicar el polling de 60s.
export default function Sidebar({ collapsed = false, onToggleCollapse, badges, chatPreview }) {
  const { user, clientes, clienteActivo, elegirCliente, logout } = useAuth();
  const { pathname } = useLocation();
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const switcherRef = useRef(null);
  const userMenuRef = useRef(null);

  // Click outside cierra dropdowns
  useEffect(() => {
    function handler(e) {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) setSwitcherOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    function escHandler(e) {
      if (e.key === "Escape") { setSwitcherOpen(false); setUserMenuOpen(false); }
    }
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", escHandler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", escHandler);
    };
  }, []);

  const hasMultipleClients = (clientes?.length || 0) > 1;
  const W = collapsed ? "w-20" : "w-[264px]";

  // El badge de Trabajo suma tareas que necesitan atención + pedidos
  // pendientes, porque ambos viven bajo la misma zona.
  const trabajoBadge = (badges.tareas_atencion + badges.pedidos_pendientes) > 0
    ? badges.tareas_atencion + badges.pedidos_pendientes : null;
  const dotDe = { plan: badges.plan_nuevo, resultados: badges.reporte_nuevo };

  return (
    <aside className={`hidden sm:flex flex-col ${W} bg-[var(--paper)] fixed left-0 top-0 bottom-0 z-[var(--z-nav)] transition-[width] duration-200 px-[14px] pt-[18px] pb-3`}>
      {/* Cabecera con logo + nombre del cliente */}
      <div className="relative pb-[18px]" ref={switcherRef}>
        <button
          onClick={() => hasMultipleClients ? setSwitcherOpen(o => !o) : null}
          className={`w-full flex items-center gap-2.5 ${collapsed ? "justify-center" : "px-1.5"} py-1 rounded-[16px] ${hasMultipleClients ? "hover:bg-[var(--line)]" : "cursor-default"} transition-colors`}
        >
          {clienteActivo?.logo_url ? (
            <img src={clienteActivo.logo_url} alt={clienteActivo.nombre}
                 className="w-11 h-11 rounded-[14px] object-contain flex-shrink-0 bg-white"
                 onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            // El color de marca del cliente sobrevive solo en su avatar
            <div className="w-11 h-11 rounded-[14px] flex items-center justify-center text-white text-lg font-extrabold flex-shrink-0"
                 style={{ background: clienteActivo?.color_primario || "#111318" }}>
              {(clienteActivo?.nombre || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-[16px] font-extrabold text-[var(--ink)] truncate leading-tight">
                  {clienteActivo?.nombre || "Cliente"}
                </div>
                <div className="text-[12px] font-semibold text-[var(--muted)] truncate">con The Crew Agencia</div>
              </div>
              {hasMultipleClients && (
                <Icon name="expand_more" size={20} className={`text-[var(--muted)] transition-transform ${switcherOpen ? "rotate-180" : ""}`}/>
              )}
            </>
          )}
        </button>

        {switcherOpen && hasMultipleClients && !collapsed && (
          <div className="shadow-overlay absolute top-full left-0 right-0 mt-1 bg-[var(--raised)] rounded-[20px] p-1.5 z-50 max-h-72 overflow-y-auto">
            {clientes.map(c => (
              <button
                key={c.id}
                onClick={() => { elegirCliente(c.id); setSwitcherOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-[14px] hover:bg-[var(--panel)] ${c.id === clienteActivo?.id ? "bg-[var(--accent-soft)]" : ""}`}
              >
                {c.logo_url ? (
                  <img src={c.logo_url} alt="" className="w-8 h-8 rounded-[10px] object-contain flex-shrink-0 bg-white"/>
                ) : (
                  // contador · iniciales del cliente en avatar chico, excepción al piso de 13px
                  <div className="w-8 h-8 rounded-[10px] flex items-center justify-center text-white text-[12px] font-extrabold flex-shrink-0"
                       style={{ background: c.color_primario || "#111318" }}>
                    {(c.nombre || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <span className="text-sm font-bold text-[var(--ink-2)] truncate flex-1 text-left">{c.nombre}</span>
                {c.id === clienteActivo?.id && <span className="text-[var(--accent)] text-[13px]">●</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Nav — 5 zonas planas */}
      <nav className="space-y-1" aria-label="Zonas del portal">
        {ZONAS.map(z => (
          <SidebarItem key={z.key} zona={z} active={z.isActive(pathname)} collapsed={collapsed}
                       badge={z.key === "trabajo" ? trabajoBadge : null} dot={!!dotDe[z.key]}/>
        ))}
      </nav>

      {/* Conversación — preview persistente del chat con el EC. Chat.jsx sigue
          existiendo como página completa. */}
      <div className="flex-1 overflow-y-auto pt-1">
        {!collapsed && (
          <div className="px-3 pt-[22px] pb-1.5 text-[13px] font-extrabold text-[var(--muted)]">Conversación</div>
        )}
        <Link to="/chat"
              title={collapsed ? "Chat" : undefined}
              className={`flex items-center ${collapsed ? "justify-center h-[46px] rounded-[16px] mt-3 hover:bg-[var(--line)]" : "gap-2.5 p-3 rounded-[14px] bg-[var(--surface)] hover:shadow-[0_1px_3px_rgba(17,19,24,0.08)]"} transition`}>
          {collapsed ? (
            <span className="relative">
              <Icon name="chat_bubble" size={22} className="text-[var(--ink-2)]"/>
              {chatPreview?.unread && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[var(--notif)]"/>}
            </span>
          ) : (
            <>
              <span className="w-9 h-9 rounded-full bg-[var(--accent-soft)] text-[var(--accent)] text-[12px] font-extrabold flex items-center justify-center flex-shrink-0">
                {iniciales(chatPreview?.nombre || "Tu Ejecutiva")}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-extrabold text-[var(--ink)] truncate">
                  {chatPreview?.nombre || "Tu Ejecutiva de Cuentas"}
                </div>
                <div className="text-[13px] text-[var(--muted)] truncate">
                  {chatPreview?.texto || "Escribile al equipo"}
                </div>
              </div>
              {chatPreview?.unread && <span className="w-[9px] h-[9px] rounded-full bg-[var(--notif)] flex-shrink-0"/>}
            </>
          )}
        </Link>
      </div>

      {/* Pie: menú de usuario */}
      <div className="pt-2 relative" ref={userMenuRef}>
        <button
          onClick={() => setUserMenuOpen(o => !o)}
          className={`w-full flex items-center gap-2.5 ${collapsed ? "justify-center" : "px-2"} py-2 rounded-[16px] hover:bg-[var(--line)] transition-colors`}
        >
          <div className="w-9 h-9 rounded-full bg-[var(--ink)] flex items-center justify-center text-[var(--surface)] text-[14px] font-extrabold flex-shrink-0">
            {(user?.nombre || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          {!collapsed && (
            <>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-bold text-[var(--ink)] truncate">{user?.nombre || "Usuario"}</div>
                <div className="text-[13px] text-[var(--muted)] truncate">{user?.email}</div>
              </div>
              <Icon name="expand_more" size={20} className={`text-[var(--muted)] transition-transform ${userMenuOpen ? "rotate-180" : ""}`}/>
            </>
          )}
        </button>

        {userMenuOpen && !collapsed && (
          <div className="shadow-overlay absolute bottom-full left-0 right-0 mb-1 bg-[var(--raised)] rounded-[20px] p-1.5 z-50">
            <Link to="/cuenta/configuracion" onClick={() => setUserMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3 h-11 rounded-[14px] text-sm font-bold text-[var(--ink-2)] hover:bg-[var(--panel)]">
              <Icon name="settings" size={20}/> Configuración
            </Link>
            <button onClick={() => { setUserMenuOpen(false); logout(); }}
                    className="w-full flex items-center gap-2.5 px-3 h-11 rounded-[14px] text-sm font-bold text-[var(--danger)] hover:bg-[var(--danger-soft)]">
              <Icon name="logout" size={20}/> Cerrar sesión
            </button>
          </div>
        )}

        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? "Expandir" : "Colapsar"}
            aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
            className="absolute -right-[22px] top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[var(--surface)] shadow-[0_1px_3px_rgba(17,19,24,0.12)] flex items-center justify-center hover:bg-[var(--panel)] transition-colors"
          >
            <Icon name="chevron_right" size={16} className={`text-[var(--muted)] ${collapsed ? "" : "rotate-180"}`}/>
          </button>
        )}
      </div>
    </aside>
  );
}

function SidebarItem({ zona, active, collapsed, badge, dot }) {
  return (
    <NavLink
      to={zona.to}
      end={zona.to === "/"}
      data-tour={zona.tour}
      aria-current={active ? "page" : undefined}
      {...(zona.prefetch ? withPrefetch(zona.prefetch) : {})}
      title={collapsed ? zona.label : undefined}
      className={`flex items-center gap-3 h-[46px] ${collapsed ? "justify-center" : "px-[14px]"} rounded-[16px] text-[16px] transition-colors ${
        active
          ? "bg-[var(--ink)] text-[var(--surface)] font-extrabold"
          : "text-[var(--ink-2)] font-semibold hover:bg-[var(--line)]"
      }`}
    >
      <span className="relative flex-shrink-0 flex">
        <Icon name={zona.icon} size={22} fill={active}/>
        {collapsed && (badge != null || dot) && (
          <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[var(--notif)]"/>
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate flex-1">{zona.label}</span>
          {badge !== null && badge !== undefined && (
            // contador · badge de conteo por sección, excepción al piso de 13px
            <span className="min-w-[24px] h-6 px-[7px] rounded-full bg-[var(--accent-line)] text-[var(--accent-ink)] text-[12px] font-extrabold flex items-center justify-center">
              {badge}
            </span>
          )}
          {dot && !badge && <span className="w-2 h-2 rounded-full bg-[var(--notif)] flex-shrink-0"/>}
        </>
      )}
    </NavLink>
  );
}
